import React, { useCallback, useEffect } from 'react';
import BooleanQueryEditor, { QueryErrorStatus, BooleanQueryEditorProps } from './BooleanQueryEditor';
import APIRequest, { APIError } from 'system/APIRequest';
import { Box } from '@mui/material';

export default function BooleanQueryField(props: BooleanQueryEditorProps) : JSX.Element {

  const [queryError, setQueryError] = React.useState<string | undefined>(undefined);
  const [lastCheckedContents, setLastCheckedContents] = React.useState<string>("");

  const checkForErrors = useCallback(() => {
    if (!props.content || !props.content.trim()) {
      setQueryError(undefined);
      return;
    }

    // If the content hasn't changed since we last checked, don't check again
    if (props.content === lastCheckedContents) {
      return;
    }

    setLastCheckedContents(props.content);
    APIRequest("query/test", {"transform": "empty"}, {
      method: "POST",
      body: JSON.stringify({
        "query": {
          op: "boolean",
          fields: ["body"],
          value: props.content
        },
      })
    }, false, 3, [400]).then((response) => {
      setQueryError(undefined);
    }).catch((err: APIError) => {
      setQueryError(err.server_message);
    });
  }, [props.content]);

  // Debounced error checking
  useEffect(() => {
    // Only check for errors 0.5s after the last input change
    const errorTimeout = setTimeout(checkForErrors, 500);
    // Clear the timeout anytime the dependencies change to debounce the error checking
    return () => clearTimeout(errorTimeout);
  }, [props.content]);

  const errorStatus = lastCheckedContents !== props.content ? QueryErrorStatus.LOADING : (queryError ? QueryErrorStatus.ERROR : QueryErrorStatus.VERIFIED);

  // Remove everything before the third ":" in the error message,
  // which is usually "There is an error with your request: error in query: `boolean` field contains error:"
  const queryText = queryError?.split(":").slice(3).join(":").trim() || queryError;

  return (
    <Box>
      <BooleanQueryEditor {...props}
        height={props.height}
        errorStatus={errorStatus}
        actions={props.actions}
        setContent={(content: string) => {
          if (props.setContent) {
            props.setContent(content);
          }
        }}
      />
      {queryError && (
        <Box sx={{ color: 'error.main', mt: 1 }}>
          {queryText}
        </Box>
      )}
    </Box>
  );
}